import { SearchService } from "@/client"
import { formatDateTime } from "@/utils/format-date"
import { stripOptionPrefix } from "@/utils/text-format"
import { Badge, Box, Flex, Spinner, Stack, Text } from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"

interface MyAnswersPanelProps {
  questionId: string
  isOpen: boolean
  options?: Array<string> | null
}

/**
 * The caller's own answer history for one searched question.
 *
 * Nothing is fetched until `isOpen` turns true: a results page can hold dozens
 * of cards, and each panel is one request.
 */
export function MyAnswersPanel({
  questionId,
  isOpen,
  options,
}: MyAnswersPanelProps) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["search-my-answers", questionId],
    queryFn: () => SearchService.listMyAnswersForQuestion({ questionId }),
    enabled: isOpen,
  })

  if (!isOpen) return null

  const answers = data?.data ?? []

  return (
    <Box
      borderWidth="1px"
      borderColor="border"
      borderRadius="md"
      bg="bg.subtle"
      p={3}
      aria-label="My answers"
    >
      <Text fontSize="xs" fontWeight="semibold" color="fg.muted" mb={2}>
        Your answers
      </Text>
      {isLoading ? (
        <Flex align="center" gap={2}>
          <Spinner size="sm" />
          <Text fontSize="sm" color="fg.muted">
            Loading your answers…
          </Text>
        </Flex>
      ) : isError ? (
        <Text fontSize="sm" color="fg.error">
          Your answers could not be loaded.
        </Text>
      ) : answers.length === 0 ? (
        <Text fontSize="sm" color="fg.muted">
          You have not answered this question yet.
        </Text>
      ) : (
        <Stack gap={2}>
          {answers.map((answer) => {
            const chosen = options?.[answer.selected_answer]
            return (
              <Flex
                key={answer.attempt_id}
                gap={3}
                align="flex-start"
                justify="space-between"
                wrap="wrap"
              >
                <Box minW="0">
                  <Text fontSize="sm" fontWeight="medium">
                    {answer.quiz_name}
                  </Text>
                  <Text fontSize="sm" color="fg.muted">
                    {String.fromCharCode(65 + answer.selected_answer)}.{" "}
                    {chosen ? stripOptionPrefix(chosen) : "Answer hidden"}
                  </Text>
                </Box>
                <Flex gap={2} align="center" flexShrink={0}>
                  <Badge
                    colorPalette={answer.is_correct ? "green" : "red"}
                    variant="subtle"
                  >
                    {answer.is_correct ? "Correct" : "Incorrect"}
                  </Badge>
                  <Text fontSize="xs" color="fg.muted">
                    {formatDateTime(answer.answered_at)}
                  </Text>
                </Flex>
              </Flex>
            )
          })}
        </Stack>
      )}
    </Box>
  )
}
